import Button from '@/components/base/Button';
import Switch from '@/components/base/Switch';
import Tabs from '@/components/base/Tabs';
import { formatMoney } from '@/utils/format';
import { channelDescription, type BulkTotals } from '@/utils/bulkPayments';
import type {
  BulkCandidate,
  BulkChannel,
  BulkRecipient,
  FundingWallet,
  SavedRecipientList,
} from '@/types/portal';
import SourceWalletPanel from './SourceWalletPanel';
import FileUploadPanel from './FileUploadPanel';
import ManualEntryPanel from './ManualEntryPanel';
import CampaignPullPanel, { type CampaignOption } from './CampaignPullPanel';
import RecipientRoster from './RecipientRoster';
import SavedListsBar from './SavedListsBar';
import RecipientReadiness from './RecipientReadiness';

export type BulkMethod = 'file' | 'manual' | 'campaign';

interface UploadStepProps {
  wallets: FundingWallet[];
  walletId: string;
  onWallet: (id: string) => void;
  channel: BulkChannel;
  onChannel: (channel: BulkChannel) => void;
  currency: string;
  balance: number;
  method: BulkMethod;
  onMethod: (method: BulkMethod) => void;
  recipients: BulkRecipient[];
  candidates: BulkCandidate[];
  /** Hired creators who aren't payable yet, with the reason. */
  waiting: BulkCandidate[];
  campaigns: CampaignOption[];
  description: string;
  onDescription: (value: string) => void;
  uniformAmount: number;
  onUniformAmount: (value: number) => void;
  onApplyUniform: () => void;
  onAdd: (candidate: BulkCandidate) => void;
  onFileParse: (fileName: string) => void;
  onAddFromCampaign: (candidates: BulkCandidate[]) => void;
  /** Payable creators for this wallet, including ones already in the batch. */
  readyCandidates: BulkCandidate[];
  /** Everyone on a campaign who can't be paid yet, including creators not hired yet. */
  campaignWaiting: BulkCandidate[];
  onWalletId: (id: string, value: string) => void;
  onAmount: (id: string, amount: number) => void;
  onRowDescription: (id: string, value: string) => void;
  onRemove: (id: string) => void;
  sms: boolean;
  onSms: (value: boolean) => void;
  savedLists: SavedRecipientList[];
  onLoadList: (list: SavedRecipientList) => void;
  onSaveList: (name: string) => void;
  totals: BulkTotals;
  onPreview: () => void;
  onDownloadTemplate: (format: 'excel' | 'csv') => void;
  canManage: boolean;
}

const methodTabs = [
  { id: 'file', label: 'File Upload', icon: 'ri-file-upload-line' },
  { id: 'manual', label: 'Manual Entry', icon: 'ri-list-check-2' },
  { id: 'campaign', label: 'From Campaigns', icon: 'ri-megaphone-line' },
];

export default function UploadStep({
  wallets,
  walletId,
  onWallet,
  channel,
  onChannel,
  currency,
  balance,
  method,
  onMethod,
  recipients,
  candidates,
  waiting,
  campaigns,
  description,
  onDescription,
  uniformAmount,
  onUniformAmount,
  onApplyUniform,
  onAdd,
  onFileParse,
  onAddFromCampaign,
  readyCandidates,
  campaignWaiting,
  onWalletId,
  onAmount,
  onRowDescription,
  onRemove,
  sms,
  onSms,
  savedLists,
  onLoadList,
  onSaveList,
  totals,
  onPreview,
  onDownloadTemplate,
  canManage,
}: UploadStepProps) {
  const totalMoney = formatMoney(totals.total, currency || 'KES');

  return (
    <div className="mt-6">
      <div className="space-y-6">
        <section className="rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
              <i className="ri-wallet-3-line" />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">
                Bulk Transaction
              </h3>
              <p className="mt-0.5 text-xs text-foreground-500">
                Set the source, the channel, then build your recipient list.
              </p>
            </div>
          </header>

          <div className="space-y-5 px-5 py-5">
            <SourceWalletPanel
              wallets={wallets}
              walletId={walletId}
              onWallet={onWallet}
              channel={channel}
              onChannel={onChannel}
              currency={currency}
              balance={balance}
            />

            <div className="border-t border-background-100 pt-5">
              <Tabs items={methodTabs} value={method} onChange={(id) => onMethod(id as BulkMethod)} />
              <p className="mt-2 text-xs text-foreground-500">{channelDescription(channel)}</p>
              {currency ? (
                <div className="mt-3">
                  <RecipientReadiness ready={candidates.length} waiting={waiting} />
                </div>
              ) : null}

              <div className="mt-4">
                {method === 'file' ? (
                  <FileUploadPanel
                    channel={channel}
                    onParse={onFileParse}
                    onDownloadTemplate={onDownloadTemplate}
                  />
                ) : null}
                {method === 'manual' ? (
                  <ManualEntryPanel
                    candidates={candidates}
                    waiting={waiting}
                    channel={channel}
                    currency={currency}
                    description={description}
                    onDescription={onDescription}
                    uniformAmount={uniformAmount}
                    onUniformAmount={onUniformAmount}
                    onApplyUniform={onApplyUniform}
                    onAdd={onAdd}
                  />
                ) : null}
                {method === 'campaign' ? (
                  <CampaignPullPanel
                    campaigns={campaigns}
                    channel={channel}
                    ready={readyCandidates}
                    waiting={campaignWaiting}
                    addedIds={recipients.map((row) => row.id)}
                    onAdd={onAddFromCampaign}
                  />
                ) : null}
              </div>
            </div>

            <div className="space-y-4 border-t border-background-100 pt-5">
              <SavedListsBar lists={savedLists} onLoad={onLoadList} onSave={onSaveList} />
              {recipients.length > 0 ? (
                <RecipientRoster
                  rows={recipients}
                  currency={currency}
                  onWalletId={onWalletId}
                  onAmount={onAmount}
                  onDescription={onRowDescription}
                  onRemove={onRemove}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-5 py-10 text-center">
                  <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-lg text-foreground-500">
                    <i className="ri-user-add-line" />
                  </span>
                  <p className="mt-3 text-sm font-medium text-foreground-800">
                    No recipients yet
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    Upload a file, add recipients manually, or pull a campaign list.
                  </p>
                </div>
              )}
            </div>
          </div>

          <footer className="flex flex-col gap-3 border-t border-background-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground-600">
              <span>
                Recipients: <strong className="text-foreground-950">{recipients.length}</strong>
              </span>
              <span>
                Total: <strong className="text-foreground-950">{totalMoney}</strong>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-2">
                <Switch checked={sms} onChange={onSms} label="Send SMS to recipients" />
                <span className="text-xs text-foreground-600">Send SMS to recipients</span>
              </span>
              <Button
                icon="ri-eye-line"
                onClick={onPreview}
                disabled={!canManage || totals.validCount === 0}
              >
                Preview Transactions
              </Button>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}